import { Button } from '@heidi/design-system/button';
import { FramedIcon, type FramedIconElement } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';

import { UpgradeCtaButton } from '../upgrade-cta-button/upgrade-cta-button';

export type ComplimentaryTrialEndDialogAction = {
  label: ReactNode;
  onClick: () => void;
  testId: string;
};

export type ComplimentaryTrialEndDialogProps = {
  open: boolean;
  onDismiss: () => void;
  testId: string;
  /** Rendered in the blue framed icon above the title. */
  icon: FramedIconElement;
  title: ReactNode;
  description: ReactNode;
  offer?: ReactNode;
  primaryAction: ComplimentaryTrialEndDialogAction;
  secondaryAction?: ComplimentaryTrialEndDialogAction;
  footnote: ReactNode;
};

/**
 * Shared layout for the complimentary Max usage upsells, before and after the
 * usage ends. Presentational: callers own copy, analytics and the Apple guard.
 */
export const ComplimentaryTrialEndDialog = ({
  open,
  onDismiss,
  testId,
  icon,
  title,
  description,
  offer,
  primaryAction,
  secondaryAction,
  footnote,
}: ComplimentaryTrialEndDialogProps) => {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onDismiss();
  };

  return (
    <DialogDrawer open={open} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="flex flex-col items-center gap-6 rounded-2xl p-8 text-center sm:max-w-[470px]"
        data-testid={testId}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex w-full flex-col items-center gap-4">
          <FramedIcon size="lg" color="blue" icon={icon} />
          <DialogDrawerTitle
            className={cn(typographyVariant({ variant: 'h2' }), 'text-text-primary')}
          >
            {title}
          </DialogDrawerTitle>
          <DialogDrawerDescription
            className={cn(typographyVariant({ variant: 'p' }), 'text-text-secondary')}
          >
            {description}
          </DialogDrawerDescription>
        </div>

        {offer}

        <div className="flex w-full flex-col gap-2">
          <UpgradeCtaButton
            tier="clinicianMax"
            size="lg"
            className="w-full"
            label={primaryAction.label}
            onClick={primaryAction.onClick}
            data-testid={primaryAction.testId}
          />
          {secondaryAction ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full"
              onClick={secondaryAction.onClick}
              data-testid={secondaryAction.testId}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>

        <TypographyCaption className="text-text-secondary">{footnote}</TypographyCaption>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
